import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, ArrowLeft, Link as LinkIcon, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import PhotoGallery from '../components/PhotoGallery';
import VideoGallery, { Video, VideoGalleryHandle } from '../components/VideoGallery';
import ConfirmDialog from '../components/ConfirmDialog';
import { getImageUrl } from '../services/imageService';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/BuilderDetails.css';
import '../styles/Buttons.css';

interface BuilderAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Builder {
  _id: string;
  workspaceId: string;
  name: string;
  description?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: {
    number: string;
    formatted: string;
  };
  contactWebsite?: string;
  address?: BuilderAddress;
  logoId?: string;
  logoUrl?: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

const BuilderDetails: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId, builderId } = useParams<{ workspaceId: string; builderId: string }>();
  const [builder, setBuilder] = useState<Builder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<any>({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const videoGalleryRef = React.useRef<VideoGalleryHandle>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (workspaceId && builderId) {
      fetchBuilder();
      fetchVideos();
    }
  }, [workspaceId, builderId]);

  const fetchBuilder = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/workspaces/${workspaceId}/builders/${builderId}`);
      const builderData: Builder = response.data.builder;
      setBuilder(builderData);

      // Fetch logo URL if logoId exists
      if (builderData.logoId && workspaceId) {
        try {
          const url = await getImageUrl(workspaceId, builderData.logoId);
          setLogoUrl(url);
        } catch (err) {
          console.error('Error fetching logo URL:', err);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch builder details');
      console.error('Error fetching builder:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/workspaces/${workspaceId}/builders`);
  };

  const fetchVideos = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/builder/${builderId}/videos`);
      // Map backend videos to frontend Video type
      const backendVideos = response.data.videos || [];
      const mappedVideos: Video[] = backendVideos.map((v: any) => ({
        _id: v._id,
        title: v.title || 'Untitled Video',
        url: v.url,
        type: v.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: v.thumbnailUrl,
        createdAt: v.createdAt
      }));
      setVideos(mappedVideos);
    } catch (err: any) {
      console.error('Error fetching videos:', err);
      // Don't show error for videos, just log it
    }
  };

  const handleAddVideo = async (video: Omit<Video, '_id' | 'createdAt'>) => {
    try {
      const response = await api.post(`/workspaces/${workspaceId}/builder/${builderId}/videos`, video);
      // Map the backend video to frontend Video type
      const backendVideo = response.data.video;
      const mappedVideo: Video = {
        _id: backendVideo._id,
        title: backendVideo.title || 'Untitled Video',
        url: backendVideo.url,
        type: backendVideo.source as 'youtube' | 'vimeo' | 'mp4' | 'other',
        thumbnail: backendVideo.thumbnailUrl,
        createdAt: backendVideo.createdAt
      };
      setVideos(prev => [...prev, mappedVideo]);
    } catch (err: any) {
      console.error('Error adding video:', err);
      throw err;
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/videos/${videoId}`);
      setVideos(prev => prev.filter(v => v._id !== videoId));
    } catch (err: any) {
      console.error('Error deleting video:', err);
      throw err;
    }
  };

  const handleUpdateVideo = async (videoId: string, title: string) => {
    try {
      await api.put(`/workspaces/${workspaceId}/videos/${videoId}`, { title });
      setVideos(prev => prev.map(v =>
        v._id === videoId ? { ...v, title } : v
      ));
    } catch (err: any) {
      console.error('Error updating video:', err);
      throw err;
    }
  };

  const startEditing = (section: string) => {
    setEditingSection(section);
    if (section === 'details') {
      setEditedData({
        name: builder?.name || '',
        contactEmail: builder?.contactEmail || '',
        contactPhone: builder?.contactPhone?.formatted || '',
        contactWebsite: builder?.contactWebsite || '',
        street1: builder?.address?.street1 || '',
        street2: builder?.address?.street2 || '',
        city: builder?.address?.city || '',
        state: builder?.address?.state || '',
        zip: builder?.address?.zip || '',
        country: builder?.address?.country || 'Canada'
      });
    } else if (section === 'description') {
      setEditedData({
        description: builder?.description || ''
      });
    }
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditedData({});
  };

  const saveChanges = async () => {
    try {
      const payload: any = {};

      if (editingSection === 'details') {
        payload.name = editedData.name;
        payload.contactEmail = editedData.contactEmail || undefined;
        payload.contactWebsite = editedData.contactWebsite || undefined;

        if (editedData.contactPhone) {
          payload.contactPhone = {
            number: editedData.contactPhone.replace(/\D/g, ''),
            formatted: editedData.contactPhone
          };
        }

        if (editedData.street1 || editedData.city) {
          payload.address = {
            street1: editedData.street1,
            street2: editedData.street2 || undefined,
            city: editedData.city,
            state: editedData.state,
            zip: editedData.zip,
            country: editedData.country
          };
        }
      } else if (editingSection === 'description') {
        payload.description = editedData.description || undefined;
      }

      await api.put(`/workspaces/${workspaceId}/builders/${builderId}`, payload);

      setEditingSection(null);
      setEditedData({});
      fetchBuilder();
    } catch (err: any) {
      console.error('Error saving changes:', err);
      setError(err.response?.data?.message || 'Failed to save changes');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleLogoClick = () => {
    logoInputRef.current?.click();
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !workspaceId || !builderId) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');

      // Step 1: Upload the image to get imageId
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await api.post(
        `/workspaces/${workspaceId}/upload/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const imageId = uploadResponse.data.image._id;

      // Step 2: Update builder with new logoId
      await api.put(`/workspaces/${workspaceId}/builders/${builderId}`, {
        logoId: imageId,
      });

      // Refresh builder data to get new logo
      await fetchBuilder();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload logo');
      console.error('Error uploading logo:', err);
    } finally {
      setUploadingLogo(false);
      // Reset input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteBuilder = async () => {
    if (!workspaceId || !builderId) return;

    try {
      setDeleting(true);
      await api.delete(`/workspaces/${workspaceId}/builders/${builderId}`);
      toast.success('Builder deleted successfully');
      navigate(`/workspaces/${workspaceId}/builders`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete builder');
      console.error('Error deleting builder:', err);
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="builder-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </AppLayout>
    );
  }

  if (error || !builder) {
    return (
      <AppLayout onBack={handleBack}>
        <div className="builder-details-error">
          <p>{error || 'Builder not found'}</p>
          <button className="btn btn-primary btn-md" onClick={handleBack}>
            Back to Builders
          </button>
        </div>
      </AppLayout>
    );
  }

  const formatAddress = (addr?: BuilderAddress) => {
    if (!addr) return 'No address provided';
    const parts = [addr.street1];
    if (addr.street2) parts.push(addr.street2);
    parts.push(addr.city);
    parts.push(addr.state);
    parts.push(addr.zip);
    parts.push(addr.country);
    return parts.filter(p => p).join(', ');
  };

  return (
    <AppLayout onBack={handleBack}>
      <div className="builder-details-container">
        {/* Header Card */}
        <div className="builder-details-header-card">
          <button className="builder-details-back-btn" onClick={handleBack}>
            <ArrowLeft size={24} />
          </button>
          <div className="builder-details-header-content">
            <div className="builder-details-logo-section">
              {uploadingLogo ? (
                <div className="builder-details-logo-placeholder">
                  <div className="builders-loading-spinner" style={{ width: '32px', height: '32px' }} />
                </div>
              ) : logoUrl ? (
                <img src={logoUrl} alt={builder.name} className="builder-details-logo-img" />
              ) : (
                <div className="builder-details-logo-placeholder">
                  {builder.name.substring(0, 8).toLowerCase()}
                </div>
              )}
              <button
                className="builder-details-logo-edit-btn"
                onClick={handleLogoClick}
                disabled={uploadingLogo}
                title="Change logo"
              >
                <Pencil size={16} />
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </div>
            <div className="builder-details-header-text">
              <h1>{builder.name}</h1>
              <p>{formatAddress(builder.address)}</p>
            </div>
          </div>
          <button
            className="builder-details-delete-btn"
            onClick={() => setShowDeleteDialog(true)}
            title="Delete builder"
          >
            <Trash2 size={20} />
          </button>
        </div>

        {/* Builder Details and Description Row */}
        <div className="builder-details-row">
          {/* Builder Details Section */}
          <div className="builder-details-card">
          <div className="builder-details-card-header">
            <h2>Builder Details</h2>
            {editingSection !== 'details' ? (
              <button
                className="builder-details-edit-btn"
                onClick={() => startEditing('details')}
              >
                <Pencil size={16} />
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary btn-sm" onClick={cancelEditing}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={saveChanges}>
                  Save
                </button>
              </div>
            )}
          </div>

          {editingSection === 'details' ? (
            <div className="builder-details-edit-form">
              <div className="builder-details-form-row">
                <div className="builder-details-form-group">
                  <label>NAME:</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editedData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>
                <div className="builder-details-form-group">
                  <label>EMAIL:</label>
                  <input
                    type="email"
                    className="form-input"
                    value={editedData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  />
                </div>
              </div>
              <div className="builder-details-form-row">
                <div className="builder-details-form-group">
                  <label>PHONE:</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={editedData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                  />
                </div>
                <div className="builder-details-form-group">
                  <label>WEBSITE:</label>
                  <input
                    type="url"
                    className="form-input"
                    value={editedData.contactWebsite}
                    onChange={(e) => handleInputChange('contactWebsite', e.target.value)}
                  />
                </div>
              </div>
              <div className="builder-details-form-group-full">
                <label>ADDRESS:</label>
                <div className="builder-details-form-group" style={{ marginBottom: '8px' }}>
                  <label>STREET ADDRESS 1:</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editedData.street1}
                    onChange={(e) => handleInputChange('street1', e.target.value)}
                  />
                </div>
                <div className="builder-details-form-group" style={{ marginBottom: '8px' }}>
                  <label>STREET ADDRESS 2:</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editedData.street2}
                    onChange={(e) => handleInputChange('street2', e.target.value)}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  <div className="builder-details-form-group">
                    <label>CITY:</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editedData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                  </div>
                  <div className="builder-details-form-group">
                    <label>PROVINCE:</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editedData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                    />
                  </div>
                  <div className="builder-details-form-group">
                    <label>POSTAL CODE:</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editedData.zip}
                      onChange={(e) => handleInputChange('zip', e.target.value)}
                    />
                  </div>
                  <div className="builder-details-form-group">
                    <label>COUNTRY:</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editedData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="builder-details-grid">
              <div className="builder-details-field">
                <label>NAME:</label>
                <span>{builder.name}</span>
              </div>
              <div className="builder-details-field">
                <label>EMAIL:</label>
                <span>{builder.contactEmail || '—'}</span>
              </div>
              <div className="builder-details-field">
                <label>PHONE:</label>
                <span>{builder.contactPhone?.formatted || '—'}</span>
              </div>
              <div className="builder-details-field">
                <label>WEBSITE:</label>
                <span>
                  {builder.contactWebsite ? (
                    <a href={builder.contactWebsite} target="_blank" rel="noopener noreferrer">
                      {builder.contactWebsite}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div className="builder-details-field full-width">
                <label>ADDRESS:</label>
                <span>{formatAddress(builder.address)}</span>
              </div>
            </div>
          )}
          </div>

          {/* Description Section */}
          <div className="builder-details-card">
            <div className="builder-details-card-header">
              <h2>Description</h2>
              {editingSection !== 'description' ? (
                <button
                  className="builder-details-edit-btn"
                  onClick={() => startEditing('description')}
                >
                  <Pencil size={16} />
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={cancelEditing}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={saveChanges}>
                    Save
                  </button>
                </div>
              )}
            </div>

            {editingSection === 'description' ? (
              <div className="builder-details-edit-form">
                <RichTextEditor
                  content={editedData.description || ''}
                  onChange={(content) => handleInputChange('description', content)}
                  placeholder="Enter builder description..."
                />
              </div>
            ) : (
              <div className="builder-details-description">
                <div dangerouslySetInnerHTML={{ __html: builder.description || '<p>No description provided.</p>' }} />
              </div>
            )}
          </div>
        </div>

        {/* Photo Gallery Section */}
        <div className="builder-details-card">
          <div className="builder-details-card-header">
            <h2>Photo Gallery</h2>
          </div>
          <PhotoGallery
            workspaceId={workspaceId!}
            entityType="builder"
            entityId={builderId!}
            imageType="photo"
            allowMultiple={true}
          />
        </div>

        {/* Video Gallery Section */}
        <div className="builder-details-card">
          <div className="builder-details-card-header">
            <h2>Video Gallery</h2>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => videoGalleryRef.current?.openAddDialog()}
            >
              <LinkIcon size={16} style={{ marginRight: '6px' }} />
              Add Video
            </button>
          </div>
          <VideoGallery
            ref={videoGalleryRef}
            workspaceId={workspaceId!}
            entityType="builder"
            entityId={builderId!}
            videos={videos}
            onAddVideo={handleAddVideo}
            onDeleteVideo={handleDeleteVideo}
            onUpdateVideo={handleUpdateVideo}
            hideHeader={true}
          />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Builder"
        message={`Are you sure you want to delete "${builder.name}"? This action cannot be undone and will remove all associated data including photos and videos.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteBuilder}
        onClose={() => setShowDeleteDialog(false)}
        loading={deleting}
      />
    </AppLayout>
  );
};

export default BuilderDetails;
